import {
  buttonVariants,
  cn,
  focusHaloFlush,
  HapticSurface,
  panelMotion,
  panelRow,
  press,
  Skeleton,
} from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CircleUser, LogIn, X } from "lucide-react";
import { sessionQuery, useCurrentProfile } from "../queries";
import { AccountAvatar } from "./AccountAvatar";

/** One mark turning away as the one that replaces it comes upright. */
const swaps = "transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]";

const pictureButton = `relative grid size-9 shrink-0 place-items-center rounded-full hover:bg-foreground/[0.07] ${panelMotion} ${press} ${focusHaloFlush}`;

/**
 * Who the frame thinks you are, as the name and picture to draw.
 *
 * They come from your profile, which is read once the session says whose it
 * is. Until both answer the frame draws a skeleton rather than a guess.
 */
function useViewer() {
  const { data: session, isError } = useQuery(sessionQuery);
  const profile = useCurrentProfile(session?.user);

  if (session === null) return { kind: "none" } as const;
  if (!session) {
    return isError ? ({ kind: "unavailable" } as const) : ({ kind: "loading" } as const);
  }
  if (session.user.isAnonymous) return { kind: "known", name: "Guest", image: null } as const;
  if (profile.data) {
    return {
      kind: "known",
      name: profile.data.displayName,
      image: profile.data.avatar,
    } as const;
  }
  if (profile.isError) return { kind: "known", name: "Account", image: null } as const;
  return { kind: "loading" } as const;
}

/**
 * Your picture at the end of the bar, and nothing else. A mark beside it
 * saying an account lives here is a second thing to look at for something the
 * picture already offers.
 *
 * Once your account is open your picture is in it, at the head, so this one
 * stops being the way in and becomes the way out.
 */
export function BarAccountToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const viewer = useViewer();

  if (viewer.kind === "loading") {
    return (
      <span aria-busy className="grid size-9 place-items-center">
        <span className="sr-only">Loading account</span>
        <Skeleton className="size-7 rounded-full" />
      </span>
    );
  }

  if (viewer.kind === "unavailable") {
    return (
      <span className="grid size-9 place-items-center rounded-full text-muted-foreground">
        <span className="sr-only">Checking account</span>
        <CircleUser aria-hidden className="size-4" />
      </span>
    );
  }

  if (viewer.kind === "none") {
    return (
      <Link
        to="/login"
        className={cn(buttonVariants({ variant: "ghost" }), "text-muted-foreground")}
      >
        <CircleUser aria-hidden className="size-4" />
        <span>Sign in</span>
        <HapticSurface />
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={open ? "Close account" : "Account"}
      aria-expanded={open}
      className={pictureButton}
    >
      <span className="grid size-7 place-items-center">
        <AccountAvatar
          name={viewer.name}
          image={viewer.image}
          size={28}
          className={cn(
            "col-start-1 row-start-1",
            swaps,
            open ? "scale-0 rotate-90" : "scale-100 rotate-0",
          )}
        />
        <X
          aria-hidden
          className={cn(
            "col-start-1 row-start-1 size-4",
            swaps,
            open ? "scale-100 rotate-0" : "-rotate-90 scale-0",
          )}
        />
      </span>
      <HapticSurface />
    </button>
  );
}

/**
 * The same control at the foot of the column, where there is room to say
 * whose picture it is. Pressing it slides the column one level down to your
 * account; the way back is the arrow that arrives in the head.
 */
export function ColumnAccountToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const viewer = useViewer();

  if (viewer.kind === "loading") {
    return (
      <div aria-busy className={cn(panelRow, "pointer-events-none")}>
        <span className="sr-only">Loading account</span>
        <Skeleton className="size-6 shrink-0 rounded-full" />
        <Skeleton className="h-4 w-28" />
      </div>
    );
  }

  if (viewer.kind === "unavailable") {
    return (
      <div className={cn(panelRow, "pointer-events-none text-muted-foreground")}>
        <CircleUser aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">Checking account</span>
      </div>
    );
  }

  if (viewer.kind === "none") {
    return (
      <Link to="/login" className={cn(panelRow, "text-muted-foreground hover:text-foreground")}>
        <LogIn aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">Sign in</span>
        <HapticSurface />
      </Link>
    );
  }

  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className={panelRow}>
      <AccountAvatar name={viewer.name} image={viewer.image} size={24} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">{viewer.name}</span>
      <HapticSurface />
    </button>
  );
}
